import type { SagaIterator } from 'redux-saga';
import { put, takeEvery } from 'redux-saga/effects';

import { resourceSagaUtils, resourceStoreTypes } from '../core';
import * as actions from './actions';
import { getLastSyncDate } from './selectors';
import { transformResponseToShares, transformShareToRequestData } from './transformers';
import type { ShareProps } from './types';

type SuccessActionCreator =
  | typeof actions.getSharesSuccess
  | typeof actions.createShareSuccess
  | typeof actions.updateShareSuccess
  | typeof actions.deleteShareSuccess;

type FailureActionCreator =
  | typeof actions.getSharesFailure
  | typeof actions.createShareFailure
  | typeof actions.updateShareFailure
  | typeof actions.deleteShareFailure;

const mkQcsSyncSharesConfig = resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<ShareProps>();

const mkResourceConfig = (successAction: SuccessActionCreator, failureAction: FailureActionCreator) =>
  mkQcsSyncSharesConfig({
    resourceName: 'shares',
    resourceBaseUrl: '/datasets/shares',
    getLastSyncDate,

    transformResponseToResources: transformResponseToShares,
    transformResourceToRequestData: transformShareToRequestData,

    successAction,
    failureAction,
  });

function* getSharesActionWatcher(): SagaIterator {
  const resourceConfig = mkResourceConfig(actions.getSharesSuccess, actions.getSharesFailure);
  yield takeEvery(actions.getShares.type, resourceSagaUtils.qcsSyncGetResources<ShareProps>, resourceConfig);
}

function* createShareActionWatcher(): SagaIterator {
  const resourceConfig = mkResourceConfig(actions.createShareSuccess, actions.createShareFailure);
  yield takeEvery(actions.createShare.type, resourceSagaUtils.qcsSyncCreateResource<ShareProps>, resourceConfig);
}

function* updateShareActionWatcher(): SagaIterator {
  const resourceConfig = mkResourceConfig(actions.updateShareSuccess, actions.updateShareFailure);
  yield takeEvery(actions.updateShare.type, resourceSagaUtils.qcsSyncUpdateResource<ShareProps>, resourceConfig);
}

function* deleteShareActionWatcher(): SagaIterator {
  const resourceConfig = mkResourceConfig(actions.deleteShareSuccess, actions.deleteShareFailure);
  yield takeEvery(actions.deleteShare.type, resourceSagaUtils.qcsSyncDeleteResource<ShareProps>, resourceConfig);
}

function* createShareSuccessActionWatcher(): SagaIterator {
  yield takeEvery(actions.createShareSuccess.type, function* createSharesSuccess(): SagaIterator {
    yield put(actions.getShares());
  });
}

export default [
  getSharesActionWatcher,
  createShareActionWatcher,
  updateShareActionWatcher,
  deleteShareActionWatcher,
  createShareSuccessActionWatcher,
];
